import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { UnitMeasurment } from '../types/Types';
import axios from 'axios';

interface UnitMeasurementCacheContextType {
  // Cache management
  getUnitMeasurement: (id: string) => UnitMeasurment | null;
  getAllUnitMeasurements: () => UnitMeasurment[];
  loadAllActiveUnitMeasurements: () => Promise<void>;
  invalidateCache: () => void;
  isLoading: boolean;

  // Search functionality
  searchUnitMeasurements: (query: string) => UnitMeasurment[];
}

const UnitMeasurementCacheContext = createContext<UnitMeasurementCacheContextType | undefined>(undefined);

export const UnitMeasurementCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unitMeasurementCache, setUnitMeasurementCache] = useState<Map<string, UnitMeasurment>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  // Stale-while-revalidate: existing cache is served immediately for instant
  // paint; every load call fires a fresh refetch. inflightRef collapses N
  // concurrent callers to exactly 1 HTTP request.
  const inflightRef = useRef<Promise<void> | null>(null);

  const getUnitMeasurement = useCallback((id: string): UnitMeasurment | null => {
    return unitMeasurementCache.get(id) || null;
  }, [unitMeasurementCache]);

  const getAllUnitMeasurements = useCallback((): UnitMeasurment[] => {
    return Array.from(unitMeasurementCache.values());
  }, [unitMeasurementCache]);

  const searchUnitMeasurements = useCallback((query: string): UnitMeasurment[] => {
    const unitMeasurements = Array.from(unitMeasurementCache.values());
    if (!query.trim()) return unitMeasurements.slice(0, 50); // Limit initial results

    const lowerQuery = query.toLowerCase();
    return unitMeasurements.filter(unitMeasurement =>
      unitMeasurement.name?.toLowerCase().includes(lowerQuery) ||
      unitMeasurement.code?.toLowerCase().includes(lowerQuery)
    ).slice(0, 50); // Limit search results
  }, [unitMeasurementCache]);

  const invalidateCache = useCallback(() => {
    inflightRef.current = null;
    setUnitMeasurementCache(new Map());
  }, []);

  const loadAllActiveUnitMeasurements = useCallback(async () => {
    // Existing cache is served immediately (instant paint) while a fresh
    // refetch is always fired. inflightRef deduplicates concurrent callers.
    if (inflightRef.current) return inflightRef.current;

    const doFetch = async () => {
      setIsLoading(true);

      try {
        const apiUrl = process.env.REACT_APP_API_URL ?? '';
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Authentication token missing');

        const response = await axios.get(`${apiUrl}/api/unit-measurments/active`, {
          headers: { 'x-auth-token': token }
        });

        const unitMeasurements = response.data || [];

        // Replace cache with fresh data
        setUnitMeasurementCache(() => {
          const newCache = new Map<string, UnitMeasurment>();
          unitMeasurements.forEach((unitMeasurement: UnitMeasurment) => {
            newCache.set(unitMeasurement._id, unitMeasurement);
          });
          return newCache;
        });

      } catch {
        // Error silently caught - existing cache remains available
      } finally {
        setIsLoading(false);
        inflightRef.current = null;
      }
    };

    inflightRef.current = doFetch();
    return inflightRef.current;
  }, []);

  const value: UnitMeasurementCacheContextType = {
    getUnitMeasurement,
    getAllUnitMeasurements,
    loadAllActiveUnitMeasurements,
    invalidateCache,
    isLoading,
    searchUnitMeasurements,
  };

  return <UnitMeasurementCacheContext.Provider value={value}>{children}</UnitMeasurementCacheContext.Provider>;
};

export const useUnitMeasurementCache = (): UnitMeasurementCacheContextType => {
  const context = useContext(UnitMeasurementCacheContext);
  if (!context) {
    throw new Error('useUnitMeasurementCache must be used within UnitMeasurementCacheProvider');
  }
  return context;
};